import {
    UrlSlug,
    urlSlugToDataProductKey
} from "@/analytics/mappings/dataProductKey.ts";

import {
    RovoDevStandardPriceCard,
    ServiceCollectionFreePriceCard,
    ServiceCollectionStandardPriceCard,
    ServiceCollectionPremiumPriceCard,
    ServiceCollectionEnterprisePriceCard,
    LoomFreePriceCard,
    LoomBusinessPriceCard,
    LoomBusinessPlusAiPriceCard,
    LoomEnterprisePriceCard,
    JiraProductDiscoveryFreePriceCard,
    JiraProductDiscoveryStandardPriceCard,
    JiraProductDiscoveryPremiumPriceCard
} from "./components";
import {
    BitbucketPremiumPriceCard,
    BitbucketStandardPriceCard
} from "./components/bitbucket-price-card.tsx";
import {
    FreePriceCard,
    PremiumPriceCard,
    EnterprisePriceCard,
    StandardPriceCard
} from "./components/pricing-calculator-plan-card.tsx";
import { bitbucketOfferingSkus } from "./configs/bitbucket.pricing-calculator-config.ts";
import {
    serviceCollectionOfferingSkus,
    jiraOfferingSkus,
    confluenceOfferingSkus,
    teamworkCollectionOfferingSkus,
    guardOfferingSkus,
    strategyCollectionOfferingSkus,
    compassOfferingSkus,
    rovoDevOfferingSkus,
    loomOfferingSkus,
    jpdOfferingSkus
} from "./configs/index.ts";
import { Plan } from "./types.ts";

export interface PricingCalculatorCardProps {
    planData: Plan;
    urlSlug: UrlSlug;
    topSectionStyle?: React.CSSProperties;
    bottomSectionStyle?: React.CSSProperties;
    index: number;
    showBannerText?: boolean;
    isSingleCard?: boolean;
}

// eslint-disable-next-line complexity
export const PricingCalculatorCard = (props: PricingCalculatorCardProps) => {
    const sku = props.planData.fields.sku;
    const trackingProductKey = urlSlugToDataProductKey[props.urlSlug];

    switch (sku) {
        // Free
        case jiraOfferingSkus.free:
        case confluenceOfferingSkus.free:
        case teamworkCollectionOfferingSkus.free:
        case compassOfferingSkus.free:
        case bitbucketOfferingSkus.free:
            return (
                <FreePriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        // Standard
        case jiraOfferingSkus.standard:
        case confluenceOfferingSkus.standard:
        case teamworkCollectionOfferingSkus.standard:
        case guardOfferingSkus.standard:
        case compassOfferingSkus.standard:
            return (
                <StandardPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case bitbucketOfferingSkus.standard:
            return (
                <BitbucketStandardPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        // Premium
        case jiraOfferingSkus.premium:
        case confluenceOfferingSkus.premium:
        case teamworkCollectionOfferingSkus.premium:
        case guardOfferingSkus.premium:
        case compassOfferingSkus.premium:
            return (
                <PremiumPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case bitbucketOfferingSkus.premium:
            return (
                <BitbucketPremiumPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        // Enterprise
        case jiraOfferingSkus.enterprise:
        case confluenceOfferingSkus.enterprise:
        case teamworkCollectionOfferingSkus.enterprise:
            // @ts-ignore
            return <EnterprisePriceCard {...props} />;
        case strategyCollectionOfferingSkus.enterprise:
            // @ts-ignore
            return <EnterprisePriceCard {...props} isJpyPriceEnabled />;

        // Custom, for any tier
        case rovoDevOfferingSkus.standard:
            return (
                <RovoDevStandardPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        case serviceCollectionOfferingSkus.free:
            return (
                <ServiceCollectionFreePriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case serviceCollectionOfferingSkus.standard:
            return (
                <ServiceCollectionStandardPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case serviceCollectionOfferingSkus.premium:
            return (
                <ServiceCollectionPremiumPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case serviceCollectionOfferingSkus.enterprise:
            return (
                <ServiceCollectionEnterprisePriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        case loomOfferingSkus.free:
            return (
                <LoomFreePriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case loomOfferingSkus.business:
            return (
                <LoomBusinessPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case loomOfferingSkus.businessPlusAi:
            return (
                <LoomBusinessPlusAiPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );
        case loomOfferingSkus.enterprise:
            return (
                <LoomEnterprisePriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        case jpdOfferingSkus.free:
            return (
                <JiraProductDiscoveryFreePriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        case jpdOfferingSkus.standard:
            return (
                <JiraProductDiscoveryStandardPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        case jpdOfferingSkus.premium:
            return (
                <JiraProductDiscoveryPremiumPriceCard
                    {...props}
                    trackingProductKey={trackingProductKey}
                />
            );

        default:
            // todo: @bsparks GWP-6943 fire an event for unexpected sku
            return null;
    }
};
